import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import type { MiniSite } from '@worki/shared';

/**
 * Section 2 of the gallery page: who the business is, where it works, what it
 * does, and two figures it can stand behind. A figure the owner never filled in
 * is left off rather than printed as zero — "0 years active" reads as a warning;
 * a business founded this year is in its first year.
 */
export function AboutSection({ site, onEdit }: { site: MiniSite; onEdit?: () => void }) {
  const { t } = useTranslation();
  const years = site.foundedYear === null ? null : Math.max(1, new Date().getFullYear() - site.foundedYear);
  const tiles = [
    years === null ? null : t('miniSite.yearsActive', { count: years }),
    site.projectsCount === null ? null : t('miniSite.projectsDone', { count: site.projectsCount }),
  ].filter((tile): tile is string => tile !== null);

  return (
    <section className="space-y-4" aria-labelledby="ms-about-heading">
      <div className="flex items-center justify-between gap-2">
        <h2 id="ms-about-heading" className="text-base font-bold">
          {t('miniSite.about')}
        </h2>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            aria-label={t('miniSite.editAbout')}
            className="h-9 w-9 rounded-lg text-muted-foreground hover:bg-muted flex items-center justify-center"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </div>

      <p className="text-sm text-muted-foreground whitespace-pre-line">{site.about || t('miniSite.noDescription')}</p>

      {site.fields.length > 0 && (
        <div className="space-y-1.5">
          <h3 className="text-xs font-semibold">{t('miniSite.specialties')}</h3>
          <p className="text-sm text-muted-foreground">{site.fields.join(' | ')}</p>
        </div>
      )}

      {site.areas.length > 0 && (
        <div className="space-y-1.5">
          <h3 className="text-xs font-semibold">{t('miniSite.serviceArea')}</h3>
          <div className="flex flex-wrap gap-1.5">
            {site.areas.map((area) => (
              <span key={area} className="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs">
                {t(`miniSite.areas.${area}`)}
              </span>
            ))}
          </div>
        </div>
      )}

      {tiles.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {tiles.map((tile) => (
            <div key={tile} className="stat-card rounded-xl p-4 text-center text-sm font-bold ltr-nums">
              {tile}
            </div>
          ))}
        </div>
      )}

      <hr className="border-border/60" />
    </section>
  );
}
